import base from '../data/dayPillars/base.json'
import { type Element, type Locale } from './constants'
import { elementBalance } from './elementBalance'
import { seniorWorkHits, youthNowHits } from './report/ageTopics'
import { isAgeSpecific, isYouthAge } from './viewer'

/**
 * 일주 데이터와 맛보기 캐시 키. 서버와 클라이언트가 같이 쓴다.
 *
 * 여기에는 **언어와 무관한 값만** 둔다 (간지, 한글 독음, 로마자, 오행).
 * 문장(카드 제목·한 줄·맛보기 폴백)은 언어별 파일에 있고,
 * 클라이언트는 src/i18n/dayPillarText.ts 로 고른 언어만 받는다.
 */

export interface DayPillarBase {
  ganZhi: string
  hangul: string
  roman: string
  stemElement: Element
  branchElement: Element
}

/** 한 언어의 일주 문장 */
export interface DayPillarText {
  title: string
  line: string
  teaser: string[]
}

export type DayPillarTextTable = Record<string, DayPillarText>

export const DAY_PILLARS: DayPillarBase[] = base as DayPillarBase[]

const BY_GANZHI = new Map(DAY_PILLARS.map((d) => [d.ganZhi, d]))

export function dayPillarBase(ganZhi: string): DayPillarBase {
  const found = BY_GANZHI.get(ganZhi)
  if (!found) throw new Error(`알 수 없는 일주: ${ganZhi}`)
  return found
}

export function isDayPillar(value: unknown): value is string {
  return typeof value === 'string' && BY_GANZHI.has(value)
}

/**
 * 맛보기 문단을 만들 때 쓰는 입력.
 *
 * **이 값들이 캐시 키의 전부이자 프롬프트 입력의 전부다.**
 * 이름·나이·연애 상태 같은 개인 정보는 들어가지 않는다 (성별만 —, 처리방침 9항과 같다).
 * 언어가 키에 들어가므로 같은 사주라도 언어마다 따로 캐시된다.
 */
export interface TeaserKey {
  dayPillar: string
  strongest: Element
  weakest: Element
  locale: Locale
  /** 같은 수로 가장 많은·적은 기운 모두 (없으면 strongest·weakest 하나) */
  most?: Element[]
  least?: Element[]
  /**
   * 읽는 사람의 성별 (입력에서 고른 값). 다른 기능처럼 프롬프트 끝에 성별 줄(withGender)을 붙인다 —
   * pt 여성에게 "Sozinho"·"arrumado" 가 나갔다. 맛보기는 결과를 저장하기 전에도 보여 서버가 결과에서 읽을 수 없어 화면 값을 쓴다.
   */
  sex?: 'female' | 'male'
  /** Five-year band only, so age-sensitive copy does not share an adult cache entry. */
  ageBand?: number
}

/** 맛보기·궁합 요청에 싣는 값 — 하나씩(strongest·weakest)과 동점이면 목록(most·least) */
export function teaserBalance(counts: { element: Element; count: number }[]): { strongest: Element; weakest: Element; most?: Element[]; least?: Element[] } {
  const b = elementBalance(counts)
  return {
    strongest: b.strongest,
    weakest: b.weakest,
    ...(b.most.length > 1 ? { most: b.most } : {}),
    ...(b.least.length > 1 ? { least: b.least } : {}),
  }
}

/** 캐시 키 문자열. Supabase teaser_cache 의 기본 키. */
export function teaserCacheKey(key: TeaserKey): string {
  // v3: 맛보기를 두 문단에서 네 문단으로 늘리고 장면을 넣게 한 프롬프트부터
  // 동점이 있으면 재료가 달라지므로 열쇠도 따로 (v4 + 목록). 동점이 없으면 예전 열쇠 그대로 — 이미 만든 글을 다시 쓴다
  // 성별 줄이 붙어 재료가 달라졌다 → v5, 성별(모르면 '-')이 열쇠에 든다. 옛 v3·v4 글은 성별을 몰라 쓰지 않는다
  const most = key.most ?? [key.strongest]
  const least = key.least ?? [key.weakest]
  // v6 : "그 언어 낱말만" 줄을 더했다 (pt 맛보기에 영어가 섞였다)
  // 재료의 "간지 (한글)" 괄호·날짜 규칙을 고쳐 판을 올렸다
  // 영어·스페인어·포르투갈어 맛보기에 오행 이름 대신 성향·장면 — 판을 올렸다
  return [key.locale === 'ko' ? 'v11' : 'v13', key.dayPillar, most.join('+'), least.join('+'), key.sex ?? '-', key.locale, ...(isAgeSpecific(key.ageBand) ? [`fix4p-${key.ageBand}`] : [])].join('|')
}

const AGE_SCENES: Record<Locale, { youth: string; senior: string }> = {
  ko: { youth: '친구들과 함께할 때 먼저 할 일을 짧게 정리해 보세요.', senior: '모임이나 집안 행사에서 맡고 싶은 일을 먼저 말해 보세요.' },
  en: { youth: 'With friends, name one thing you want to do together.', senior: 'At a gathering, say which part you would like to help with.' },
  ja: { youth: '友だちと一緒のときは、まずやりたいことを短く伝えてみましょう。', senior: '集まりでは、手伝いたいことを先に伝えてみましょう。' },
  'zh-Hans': { youth: '和朋友一起时，先说一句自己想做什么。', senior: '在聚会中，先说说自己想帮什么忙。' },
  'zh-Hant': { youth: '和朋友一起時，先說一句自己想做什麼。', senior: '在聚會中，先說說自己想幫什麼忙。' },
  es: { youth: 'Cuando estés con amigos, di primero qué te gustaría hacer.', senior: 'En una reunión de amigos, di en qué te gustaría colaborar.' },
  'pt-BR': { youth: 'Entre amigos, diga primeiro o que gostaria de fazer.', senior: 'Em um encontro, diga em que gostaria de ajudar.' },
}

export function ageSafeTeaser(paragraphs: readonly string[], locale: Locale, ageBand?: number): string[] {
  if (!isAgeSpecific(ageBand)) return [...paragraphs]
  const youth = isYouthAge(ageBand)
  const hit = youth ? youthNowHits : seniorWorkHits
  const safe = AGE_SCENES[locale][youth ? 'youth' : 'senior']
  return paragraphs.map((paragraph) => hit(paragraph, locale).length > 0 ? safe : paragraph)
}

/** 일주 문장 표에서 맛보기 폴백 두 문단을 꺼낸다. */
export function fallbackTeaserFrom(table: DayPillarTextTable, ganZhi: string): string[] {
  const text = table[ganZhi]
  if (!text) throw new Error(`일주 문장이 없다: ${ganZhi}`)
  return text.teaser
}
